import { http } from '@/core/api'
import type { RecruitmentPortalGuestRegistration } from '@/features/recruitment/api/recruitment-portal.api'

export type RecruitmentPortalZaloNotifyChannel = 'gmf' | 'bot' | 'personal'

export type RecruitmentPortalZaloPersonalLink = {
  group_id: string
  name: string
  groups: Array<{ id: string; name: string }>
}

export type RecruitmentPortalQrRow = {
  id: number
  name: string
  employee_code?: string | null
  email?: string | null
  department_code?: string | null
  is_active: boolean
  guest_qr_eligible?: boolean
  public_register_enabled: boolean
  public_register_token?: string | null
  register_url?: string | null
  staff_register_url?: string | null
  login_url?: string | null
  intake_cutoff_time?: string | null
  intake_notify_enabled?: boolean
  intake_zalo_notify_channel?: RecruitmentPortalZaloNotifyChannel | null
  intake_zalo_bot_id?: number | null
  intake_zalo_bot_chat_id?: string | null
  intake_zalo_bot_label?: string | null
  intake_zalo_personal_group_ids?: string[]
  intake_zalo_personal?: RecruitmentPortalZaloPersonalLink | null
  intake_zalo_group_id?: string | null
  intake_zalo_group_name?: string | null
  intake_zalo_group_tier?: string | null
  intake_settings_source_access_qr_id?: number | null
  intake_settings_department_code?: string | null
  intake_settings_inherited?: boolean
  qr_logo_url?: string | null
}

export type RecruitmentPortalQrUpdatePayload = {
  public_register_enabled?: boolean
}

export type StaffRegistrationListParams = {
  applied_from?: string
  applied_to?: string
  interview_from?: string
  interview_to?: string
}

let myRegistrationsInFlight: {
  key: string
  promise: Promise<RecruitmentPortalGuestRegistration[]>
} | null = null

function staffRegistrationQuery(params?: StaffRegistrationListParams): StaffRegistrationListParams {
  const query: StaffRegistrationListParams = {}
  const keys = ['applied_from', 'applied_to', 'interview_from', 'interview_to'] as const
  for (const key of keys) {
    const value = params?.[key]?.trim()
    if (value) query[key] = value
  }
  return query
}

export const recruitmentPortalQrApi = {
  async list(search?: string): Promise<{ data: RecruitmentPortalQrRow[] }> {
    const { data } = await http.get<{ data: RecruitmentPortalQrRow[] }>('/recruitment/portal-qr', {
      params: search ? { search } : undefined,
    })
    return data
  },

  async me(): Promise<{ data: RecruitmentPortalQrRow }> {
    const { data } = await http.get<{ data: RecruitmentPortalQrRow }>('/recruitment/portal-qr/me')
    return data
  },

  async portal(): Promise<{ data: { staff_register_url: string } }> {
    const { data } = await http.get<{ data: { staff_register_url: string } }>(
      '/recruitment/portal-qr/portal',
    )
    return data
  },

  async setEnabledMe(enabled: boolean): Promise<{ data: RecruitmentPortalQrRow }> {
    const { data } = await http.patch<{ data: RecruitmentPortalQrRow }>('/recruitment/portal-qr/me', {
      public_register_enabled: enabled,
    })
    return data
  },

  async setEnabled(userId: number, enabled: boolean): Promise<{ data: RecruitmentPortalQrRow }> {
    const { data } = await http.patch<{ data: RecruitmentPortalQrRow }>(
      `/recruitment/portal-qr/${userId}`,
      { public_register_enabled: enabled },
    )
    return data
  },

  async updateMe(payload: RecruitmentPortalQrUpdatePayload): Promise<{ data: RecruitmentPortalQrRow }> {
    const { data } = await http.patch<{ data: RecruitmentPortalQrRow }>(
      '/recruitment/portal-qr/me',
      payload,
    )
    return data
  },

  async listMyRegistrations(params?: StaffRegistrationListParams): Promise<RecruitmentPortalGuestRegistration[]> {
    const query = staffRegistrationQuery(params)
    const key = JSON.stringify(query)
    if (myRegistrationsInFlight?.key === key) return myRegistrationsInFlight.promise
    const promise = http
      .get<{ data: RecruitmentPortalGuestRegistration[] }>(
        '/recruitment/portal-qr/me/registrations',
        {
          params: query,
          headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        },
      )
      .then(({ data }) => {
        const rows = data?.data
        return Array.isArray(rows) ? rows : []
      })
      .finally(() => {
        if (myRegistrationsInFlight?.promise === promise) myRegistrationsInFlight = null
      })
    myRegistrationsInFlight = { key, promise }
    return promise
  },

  async changeMyRegistrationFactory(
    applicationId: number,
    payload: {
      job_order_id: number
      captcha_id?: string
      captcha_answer?: string
      province_code?: string
      province?: string
      ward_code?: string
      ward?: string
    },
  ): Promise<RecruitmentPortalGuestRegistration> {
    const { data } = await http.patch<{ data: RecruitmentPortalGuestRegistration }>(
      `/recruitment/portal-qr/me/registrations/${applicationId}/factory`,
      payload,
    )
    return data.data
  },

  async update(
    userId: number,
    payload: RecruitmentPortalQrUpdatePayload,
  ): Promise<{ data: RecruitmentPortalQrRow }> {
    const { data } = await http.patch<{ data: RecruitmentPortalQrRow }>(
      `/recruitment/portal-qr/${userId}`,
      payload,
    )
    return data
  },
}
